import {
  KioskGlobalSettings,
  MultiCurrencyMode,
  UIKioskGlobalSettings,
} from '@core/models/kiosk-global-settings.model';

const PRODUCT_CARD_CHOICES = [1, 2, 3, 4, 5, 6] as const;
const PRODUCT_CARD_STYLES = ['defaultCard', 'shadowCard', 'borderedCard', 'roundedCard'] as const;

type ProductCardChoice = (typeof PRODUCT_CARD_CHOICES)[number];
type ProductCardStyle = (typeof PRODUCT_CARD_STYLES)[number];

/**
 * CB2 persists nested kiosk settings with either the legacy camelCase keys
 * (`productCard`) or the snake_case section ids (`product_card`). The studio
 * reads both; the kiosk runtime used to read only camelCase, so a saved layout
 * or card style never reached the product cards.
 */
export function normalizeKioskGlobalSettings(
  settings: Partial<KioskGlobalSettings> | Record<string, unknown> | null | undefined,
): UIKioskGlobalSettings {
  const record = asRecord(settings);
  const stepper = asRecord(
    firstPresent(record, 'stepperProductComposition', 'stepper_product_composition'),
  );
  const productCard = asRecord(firstPresent(record, 'productCard', 'product_card'));
  const styles = asRecord(productCard?.['styles']);
  const choice = normalizeProductCardChoice(productCard?.['choice']);

  const modal = asRecord(
    firstPresent(
      record,
      'orderingUnavailableModal',
      'ordering_unavailable_modal',
      'shopClosedModal',
      'shop_closed_modal',
    ),
  );

  return {
    stepperProductComposition: {
      autoAddToCart: readBoolean(stepper, 'autoAddToCart', 'auto_add_to_cart'),
      hideNextButtonIfTheRequiredQuantityIsOne: readBoolean(
        stepper,
        'hideNextButtonIfTheRequiredQuantityIsOne',
        'hide_next_button_if_the_required_quantity_is_one',
      ),
    },
    productCard: {
      choice,
      cardColSpan: choice === 4 || choice === 6 ? 2 : 1,
      styles: {
        type: normalizeProductCardStyle(styles?.['type']),
      },
    },
    orderingUnavailableModal: {
      title: readString(modal, 'title'),
      message: readString(modal, 'message'),
    },
    multiCurrency: {
      mode: normalizeMultiCurrencyMode(record),
    },
  };
}

function normalizeProductCardChoice(value: unknown): ProductCardChoice {
  const choice = typeof value === 'string' ? Number(value) : value;
  if (typeof choice === 'number' && PRODUCT_CARD_CHOICES.includes(choice as ProductCardChoice)) {
    return choice as ProductCardChoice;
  }
  return 1;
}

function normalizeProductCardStyle(value: unknown): ProductCardStyle {
  if (typeof value === 'string' && PRODUCT_CARD_STYLES.includes(value as ProductCardStyle)) {
    return value as ProductCardStyle;
  }
  return 'defaultCard';
}

function normalizeMultiCurrencyMode(
  record: Record<string, unknown> | undefined,
): MultiCurrencyMode {
  const camelMode = readMultiCurrencyMode(firstPresent(record, 'multiCurrency'));
  if (camelMode !== 'off') {
    return camelMode;
  }
  return readMultiCurrencyMode(firstPresent(record, 'multi_currency'));
}

function readMultiCurrencyMode(value: unknown): MultiCurrencyMode {
  const mode = asRecord(value)?.['mode'];
  return mode === 'sco' || mode === 'test' ? mode : 'off';
}

/** First key that is actually stored. A later alias must not be hidden by a missing camelCase key. */
function firstPresent(
  record: Record<string, unknown> | undefined,
  ...keys: readonly string[]
): unknown {
  if (!record) {
    return undefined;
  }
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(record, key) && record[key] != null) {
      return record[key];
    }
  }
  return undefined;
}

function readBoolean(
  record: Record<string, unknown> | undefined,
  ...keys: readonly string[]
): boolean {
  return firstPresent(record, ...keys) === true;
}

function readString(record: Record<string, unknown> | undefined, key: string): string {
  const value = record?.[key];
  return typeof value === 'string' ? value : '';
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
}
